"use client";

import { useEffect } from "react";
import { useTheme } from "next-themes";

/**
 * Registers real, working site actions with the WebMCP API so an in-browser
 * agent can drive them directly, instead of guessing at DOM structure.
 *
 * The spec (webmachinelearning.github.io/webmcp) defines the API on
 * `document.modelContext`. Docs referenced by AI-agent-readiness scanners
 * (developers.cloudflare.com, isitagentready.com) describe it as
 * `navigator.modelContext` — aliased below to the same real object so both
 * forms work; nothing here is a stub, both point at genuine registered tools.
 *
 * Only tools backed by a real, already-shipped UI action are registered —
 * see components/sections/hero.tsx for the contact-panel tool, which is
 * scoped to the page it actually exists on rather than declared globally.
 */
export function WebMCPInit() {
  const { setTheme } = useTheme();

  useEffect(() => {
    // Real spec location is document.modelContext, but the docs referenced by
    // agent-readiness scanners describe navigator.modelContext — check both,
    // whichever the current environment actually provides.
    const modelContext =
      (navigator as any).modelContext || (document as any).modelContext;
    if (!modelContext?.registerTool) return;

    if (!(navigator as any).modelContext) {
      (navigator as any).modelContext = modelContext;
    }

    const controller = new AbortController();

    modelContext.registerTool(
      {
        name: "set_theme",
        title: "Set site theme",
        description:
          "Switch the site's colour theme between light, dark, or the visitor's system preference.",
        inputSchema: {
          type: "object",
          properties: {
            theme: { type: "string", enum: ["light", "dark", "system"] },
          },
          required: ["theme"],
        },
        execute: async (input: { theme: "light" | "dark" | "system" }) => {
          setTheme(input.theme);
          return { ok: true, theme: input.theme };
        },
      },
      { signal: controller.signal },
    );

    return () => controller.abort();
  }, [setTheme]);

  return null;
}
